import { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { useGetAllRuns } from '@/api/control/control-plane/control-plane';
import { GetAllRunsRequestStatesItem } from '@/api/control/missionImpactAPI.schemas';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDataverseConcurrencyLimit } from '@/hooks/useDataverseConcurrencyLimit';
import {
  exceedsDataverseCapacity,
  fillTemplate,
  formatOpenEndedCount,
} from '@/lib/sourceCapacity';

/** How often to re-check for queued runs while the notice is shown, in milliseconds. */
const QUEUED_RUNS_REFRESH_MS = 60_000;

/** The maximum number of queued runs fetched to count; beyond this the count is shown as "N+". */
const QUEUED_RUNS_PAGE_SIZE = 100;

/**
 * Props for the SourceCapacityNotice component.
 */
interface SourceCapacityNoticeProps {
  /** The organization ID. */
  orgId: string;
  /** The profile ID. */
  profileId: string;
  /** The source type discriminator ($type from SourceDto). */
  sourceType: string;
  /** The number of the source's tables loaded so far. */
  tableCount: number;
  /** Whether more of the source's tables exist beyond those loaded. */
  hasMoreTables: boolean;
}

/**
 * Explains Dataverse request throttling on a profile's source tables (issue #474): a banner while
 * any of the profile's runs are queued for Dataverse capacity, and a one-line hint when the source
 * has more tables than the per-environment concurrent request limit. Renders nothing for other
 * source types or when no limit is configured.
 */
export function SourceCapacityNotice({ orgId, profileId, sourceType, tableCount, hasMoreTables }: SourceCapacityNoticeProps) {
  const { t } = useLanguage();
  const limit = useDataverseConcurrencyLimit();
  const isDataverseWithLimit = sourceType === 'Dataverse' && limit > 0;

  // Queued runs for this profile; bounded to the last day like the profile run summary.
  const startDate = useMemo(() => {
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);
    return oneDayAgo.toISOString();
  }, []);

  const { data: queuedRunsData } = useGetAllRuns(
    orgId,
    {
      pageSize: QUEUED_RUNS_PAGE_SIZE,
      profileIds: [profileId],
      states: [GetAllRunsRequestStatesItem.WaitingForSourceCapacity],
      startDate,
    },
    {
      query: {
        enabled: isDataverseWithLimit && !!orgId && !!profileId,
        refetchInterval: QUEUED_RUNS_REFRESH_MS,
      },
    },
  );

  if (!isDataverseWithLimit) {
    return null;
  }

  const queuedRuns = queuedRunsData?.data.items ?? [];
  const hasMoreQueued = queuedRunsData?.data.hasMore ?? false;
  const strings = t.sources.sourceCapacity;

  return (
    <>
      {queuedRuns.length > 0 && (
        <Alert>
          <Clock />
          <AlertTitle>{strings.queuedTitle}</AlertTitle>
          <AlertDescription>
            {fillTemplate(strings.queuedDescription, {
              count: formatOpenEndedCount(queuedRuns.length, hasMoreQueued),
              limit,
            })}
          </AlertDescription>
        </Alert>
      )}
      {exceedsDataverseCapacity(sourceType, limit, tableCount) && (
        <p className="text-sm text-muted-foreground">
          {fillTemplate(strings.manyTablesHint, { limit, count: formatOpenEndedCount(tableCount, hasMoreTables) })}
        </p>
      )}
    </>
  );
}
